import React, { useState, useEffect } from 'react';
import './Table.css';

const Table = ({
  data,
  columns,
  title,
  striped = true,
  bordered = true,
  hoverable = true,
  compact = false,
  loading = false,
  emptyMessage = "No data available",
  enableCheckboxes = false,
  enableSearch = false,
  enableSorting = true,
  enableColumnToggle = true,
  enableExport = false,
  searchPlaceholder = "Search...",
  onSelectionChange = () => { },
  onRowClick = () => { },
  externalSelections = null,
  className = ""
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Column Visibility State
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col, idx) => ({ ...acc, [idx]: true }), {})
  );
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  // Reset selections when external selection is cleared
  useEffect(() => {
    if (externalSelections && externalSelections.length === 0) {
      setSelectedRows([]);
    }
  }, [externalSelections]);

  // Sync visibleColumns when columns change (e.g. data-dependent columns added after async load).
  // Only marks NEW indices as visible — preserves any columns the user has manually toggled off.
  useEffect(() => {
    setVisibleColumns(prev => {
      const next = { ...prev };
      let changed = false;
      columns.forEach((_, idx) => {
        if (next[idx] === undefined) {
          next[idx] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [columns]);

  // Sync with new data
  useEffect(() => {
    setSelectedRows([]); // Reset internal selection state
    setFilteredData(data); // Sync with new data
    setCurrentPage(1); // Reset pagination on new data
  }, [data]);

  // Sync internal selectedRows with external data length if it's externally cleared
  // This helps when the parent clears its selection state but data doesn't change
  // We can pass a 'resetSelection' prop or just detect if onSelectionChange was called with []
  // But a cleaner way is to just allow the parent to pass the selection state back in.


  // Helper function to resolve nested properties from dot notation paths (e.g., 'user.firstName')
  const resolvePath = (object, path, defaultValue = '') => {
    if (!object || !path) return defaultValue;
    const value = path.split('.').reduce((o, p) => (o ? o[p] : undefined), object);
    return value !== undefined ? value : (object[path] !== undefined ? object[path] : defaultValue);
  };

  // Handle search filtering
  useEffect(() => {
    let results = data;
    if (searchTerm.trim() !== '') {
      const lowercasedSearch = searchTerm.toLowerCase();
      results = data.filter(row =>
        columns.some((column, idx) => {
          if (!visibleColumns[idx]) return false; // skip hidden columns
          if (!column.accessor) return false; // Skip columns without accessors
          const value = resolvePath(row, column.accessor, null);
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowercasedSearch);
        })
      );
    }

    // Apply sorting
    if (sortConfig.key !== null) {
      results = [...results].sort((a, b) => {
        let aValue = resolvePath(a, sortConfig.key, '');
        let bValue = resolvePath(b, sortConfig.key, '');

        // Handle undefined or null
        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';

        // Basic string/number comparison
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(results);
    setCurrentPage(1); // Reset pagination on filter change
  }, [searchTerm, data, columns, sortConfig, visibleColumns]);

  const handleSort = (key) => {
    if (!enableSorting) return;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      key = null; // Toggle off sorting completely
    }
    setSortConfig({ key, direction });
  };

  const currentData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize); // Render paginated data

  // CSV Export
  const exportToCSV = () => {
    // Determine headers based on visible columns
    const headers = columns
      .filter((_, idx) => visibleColumns[idx])
      .map(col => getColumnLabel(col));

    // Create CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...currentData.map(row => {
        return columns
          .filter((_, idx) => visibleColumns[idx])
          .map(col => {
            let val = col.accessor ? resolvePath(row, col.accessor, '') : '';
            if (val === null || val === undefined) val = '';
            // Escape quotes and wrap in quotes for robust CSV parsing
            return `"${String(val).replace(/"/g, '""')}"`;
          })
          .join(',');
      })
    ].join('\n');

    // Make blob & download link
    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title ? title.replace(/\s+/g, '_') : 'Export'}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle row selection
  const handleRowSelect = (rowIndex, isChecked) => {
    const newSelected = isChecked
      ? [...selectedRows, rowIndex]
      : selectedRows.filter(index => index !== rowIndex);

    setSelectedRows(newSelected);
    onSelectionChange(newSelected.map(index => filteredData[index]));
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allPageIndices = currentData
        .map((row, index) => {
          const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
          const isInactive = status === 'INACTIVE' || status === 'inactive';
          return isInactive ? null : index;
        })
        .filter(index => index !== null);
      setSelectedRows(allPageIndices);
      onSelectionChange(allPageIndices.map(index => filteredData[index]));
    } else {
      setSelectedRows([]);
      onSelectionChange([]);
    }
  };

  // Check if all active/selectable rows are selected
  const activeCurrentData = currentData.filter(row => {
    const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
    const isInactive = status === 'INACTIVE' || status === 'inactive';
    return !isInactive;
  });

  const allCurrentSelected =
    activeCurrentData.length > 0 &&
    currentData.every((row, index) => {
      const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
      const isInactive = status === 'INACTIVE' || status === 'inactive';
      return isInactive || selectedRows.includes(index);
    });

  // Handle row click
  const handleRowClickEvent = (row, dataIndex) => {
    if (!enableCheckboxes || !selectedRows.includes(dataIndex)) {
      onRowClick(row);
    }
  };

  const getColumnLabel = (column) => {
    if (typeof column.header === 'string') return column.header;
    if (column.accessor) {
      return String(column.accessor)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase());
    }
    return 'Value';
  };

  const tableClasses = [
    'reusable-table',
    striped ? 'striped' : '',
    bordered ? 'bordered' : '',
    hoverable ? 'hoverable' : '',
    compact ? 'compact' : '',
    loading ? 'loading' : '',
    className
  ].filter(Boolean).join(' ');



  const totalPages = Math.ceil(filteredData.length / pageSize);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="table-pagination-container">
        <button 
          className="pagination-arrow" 
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        {pages.map(page => (
          <button 
            key={page}
            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button 
          className="pagination-arrow" 
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="table-container">
      {/* Table Toolbar including Title, Search, Columns, and Export */}
      <div className="table-toolbar">
        {title && <h3 className="table-title">{title}</h3>}

        <div className="table-actions">
          {enableSearch && (
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          {enableColumnToggle && (
            <div className="column-toggle-container">
              <button
                className="table-action-btn"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              >
                Columns
                <span className="dropdown-arrow">{showColumnDropdown ? '▲' : '▼'}</span>
              </button>
              {showColumnDropdown && (
                <div className="column-dropdown-menu">
                  {columns.map((col, idx) => (
                    <label key={idx} className="column-dropdown-item">
                      <input
                        type="checkbox"
                        checked={!!visibleColumns[idx]}
                        onChange={() => setVisibleColumns(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      />
                      {getColumnLabel(col)}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {enableExport && (
            <button className="table-action-btn" onClick={exportToCSV}>
              Export CSV
            </button>
          )}
        </div>
      </div>

      <div className="table-wrapper">
        {loading && <div className="loading-overlay">Loading...</div>}
        <table className={tableClasses}>
          <thead>
            <tr>
              {enableCheckboxes && (
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={allCurrentSelected && currentData.length > 0}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {columns.map((column, index) => {
                if (!visibleColumns[index]) return null;
                const isSorted = sortConfig.key === (column.accessor || column.header);

                return (
                  <th
                    key={index}
                    style={{ ...column.style, cursor: (enableSorting && column.accessor) ? 'pointer' : 'default' }}
                    data-label={getColumnLabel(column)}
                    onClick={() => column.accessor ? handleSort(column.accessor) : null}
                  >
                    <div className="th-content">
                      {column.header}
                      {enableSorting && column.accessor && (
                        <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
                          {isSorted ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, rowIndex) => {
                const dataIndex = rowIndex; // Keep index logic simple
                const isSelected = selectedRows.includes(dataIndex);

                const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
                const isInactive = status === 'INACTIVE' || status === 'inactive';

                return (
                  <tr
                    key={rowIndex}
                    className={`${isSelected ? 'activeRow' : ''} ${isInactive ? 'inactiveRow' : ''}`}
                    onClick={() => handleRowClickEvent(row, dataIndex)}
                    style={{ cursor: isInactive ? 'not-allowed' : 'pointer' }}
                  >
                    {enableCheckboxes && (
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isInactive}
                          onChange={e => handleRowSelect(dataIndex, e.target.checked)}
                          onClick={e => e.stopPropagation()} // Prevent checkbox click from triggering row click
                        />
                      </td>
                    )}
                    {columns.map((column, colIndex) => {
                      if (!visibleColumns[colIndex]) return null;
                      return (
                        <td
                          key={colIndex}
                          style={column.cellStyle || column.style}
                          data-label={getColumnLabel(column)}
                        >
                          {column.render
                            ? column.render(row)
                            : row[column.accessor]}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={enableCheckboxes ? Object.values(visibleColumns).filter(Boolean).length + 1 : Object.values(visibleColumns).filter(Boolean).length}
                  className="empty-message"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filteredData.length > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginBottom: '16px' }}>
          {renderPagination()}
        </div>
      )}
    </div>
  );
};

export default Table;
